import type { ReactElement } from 'react';
import { useSwapState } from './useSwapState';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';
import { AR_BRAND, AR_BRAND_PRI, AR_BRAND_REST, type PriWord } from '@/copy/hero-pri-words';

interface TypewriterARProps {
  words: readonly PriWord[];
  prefix: string;
}

/* The brand word alone gets the two-tone split (teal PRI + ink remainder);
 * every other word is wholly teal. Compared and rendered from the shared
 * constants so this can't drift out of sync with the word list. */
function renderArSpans(word: string): ReactElement {
  if (word === AR_BRAND) {
    return (
      <>
        <span className="mkt-hero-tw__pri">{AR_BRAND_PRI}</span>
        <span className="mkt-hero-tw__ar-rest">{AR_BRAND_REST}</span>
      </>
    );
  }
  return <span className="mkt-hero-tw__pri">{word}</span>;
}

export default function TypewriterAR({ words, prefix }: TypewriterARProps) {
  const reduceMotion = usePrefersReducedMotion();
  const { phase, wordIndex, charCount } = useSwapState({ words, prefix, withClearing: true });

  if (reduceMotion) {
    const first = words[0];
    if (!first) return null;
    return (
      <div
        className="mkt-hero-tw__swap mkt-hero-tw__swap--d"
        data-testid="typewriter-ar"
        data-phase="holdAr"
      >
        <div className="mkt-hero-tw__swap-faces">
          <p className="mkt-hero-tw__swap-en" dir="ltr" aria-hidden="true" />
          <p className="mkt-hero-tw__swap-ar" dir="rtl" aria-label={first.ar}>
            {renderArSpans(first.ar)}
          </p>
        </div>
      </div>
    );
  }

  const current = words[wordIndex];
  if (!current) return null;
  const suffix = current.en.slice(prefix.length);
  const visible = suffix.slice(0, charCount);

  return (
    <div
      className="mkt-hero-tw__swap mkt-hero-tw__swap--d"
      data-testid="typewriter-ar"
      data-phase={phase}
    >
      <div className="mkt-hero-tw__swap-faces">
        <p className="mkt-hero-tw__swap-en" dir="ltr" aria-hidden="true">
          <span className="mkt-hero-tw__pri">{prefix}</span>
          <span>{visible}</span>
          <span className="mkt-hero-tw__cursor" aria-hidden="true" />
        </p>
        <p className="mkt-hero-tw__swap-ar" dir="rtl" aria-label={current.ar}>
          {renderArSpans(current.ar)}
        </p>
      </div>
    </div>
  );
}
